import { useEffect, useState, useMemo } from 'react';

import { useBuildVars, useSiteConfig, useInX4AFrame } from 'mf_core/config';
import {
  useIsAuthenticated,
  useIsImpersonationMode,
  useDecodedToken,
} from 'mf_core/auth';
import { useLocalizationContext } from '@im/react-localization';

const scriptSrcModifierMap = {
  development: 'test/',
  production: '',
  productionbeta: '',
  productionbetax: 'betax/',
  staging: 'test/',
  staging2: 'test/',
  stagingx: 'test/',
  test: 'test/',
};

export function useWalkMe(isFirstLogin: boolean) {
  const isAuthenticated = useIsAuthenticated();
  const { buildEnvironment } = useBuildVars();
  const { defaultLocale } = useSiteConfig();
  const { locale } = useLocalizationContext();
  const isImpersonationMode = useIsImpersonationMode();
  const { isInX4AFrame } = useInX4AFrame();

  const [isWalkMeUserActive, setIsWalkMeUserActive] = useState(false);
  const [walkMeInitialization, setWalkMeInitialization] = useState(false);
  const isWalkMeLoaded = window._walkmeInternals;
  const walkMeAPI = window.WalkMeAPI;

  const decodedToken = useDecodedToken();

  const tokenData = useMemo(() => {
    const firstName: string = decodedToken?.imoProfile?.firstName || '';
    const lastName: string = decodedToken?.imoProfile?.lastName || '';
    return {
      email: decodedToken?.imoProfile?.emailAddress || '',
      language: defaultLocale,
      name: `${firstName}${
        firstName && lastName ? ` ${lastName}` : `${lastName}`
      }`,
      resellerId: decodedToken?.resellerId || '',
      userRoles: decodedToken?.role,
      uuid: decodedToken?.imoProfile?.userId,
    };
  }, [decodedToken, defaultLocale]);

  const srcModifier = scriptSrcModifierMap[buildEnvironment] || '';

  useEffect(() => {
    if (isAuthenticated && tokenData && !isImpersonationMode && !isInX4AFrame) {
      if (!window.user) {
        //Initialize WalkMe globals
        window.user = {
          ...tokenData,
          uuid: window.btoa(tokenData.uuid),
        };

        setIsWalkMeUserActive(true);
      }
    }
  }, [
    defaultLocale,
    isAuthenticated,
    isImpersonationMode,
    isInX4AFrame,
    tokenData,
  ]);

  useEffect(() => {
    // Inject WalkMe Script in HTML Head
    if (
      !isFirstLogin &&
      !isImpersonationMode &&
      !isInX4AFrame &&
      buildEnvironment &&
      isAuthenticated &&
      isWalkMeUserActive
    ) {
      const walkMe = document.createElement('script');
      walkMe.type = 'text/javascript';
      walkMe.async = true;
      walkMe.src = `https://cdn.walkme.com/users/de4fe94aae764ca490170912028ce58b/${srcModifier}walkme_de4fe94aae764ca490170912028ce58b_https.js`;
      const s = document.getElementsByTagName('script')[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(walkMe, s);
        window._walkmeConfig = { smartLoad: true };
      }
    }
  }, [
    buildEnvironment,
    isAuthenticated,
    isFirstLogin,
    isImpersonationMode,
    isInX4AFrame,
    isWalkMeUserActive,
    srcModifier,
  ]);

  useEffect(() => {
    if (
      isWalkMeLoaded &&
      walkMeAPI &&
      !walkMeInitialization &&
      walkMeAPI.getCurrentLanguage() !== locale
    ) {
      //Correct WalkMe language after player loads
      const delayInit = setTimeout(() => {
        walkMeAPI.changeLanguage(locale);
      }, 1000);
      return () => {
        clearTimeout(delayInit);
        setWalkMeInitialization(true);
      };
    }
  }, [walkMeInitialization, isWalkMeLoaded, locale, walkMeAPI]);

  useEffect(() => {
    if (isWalkMeUserActive && locale && walkMeAPI) {
      //Update global language variable when locale changes
      if (window.user.language !== locale) window.user.language = locale;

      // Use WalkMe JS API to push correct language content on locale change.
      // The default language is English, represented by an empty string.
      // All other languages use a lang-country locale shortname, e.g. 'de-DE'.
      // For available languages use WalkMeAPI.getAllLanguages().
      // More info: https://developer.walkme.com/reference/multilanguage-support
      if (locale.split('-')[0] === 'en') {
        walkMeAPI.changeLanguage('');
      } else {
        walkMeAPI.changeLanguage(locale);
      }
    }
  }, [isWalkMeUserActive, locale, walkMeAPI]);

  useEffect(() => {
    // Hide the WalkMe player on welcome video page
    const walkMe = document.getElementById('walkme-player');
    if (walkMe) {
      if (isFirstLogin && walkMe) {
        walkMe.style.display = 'none';
      } else {
        walkMe.style.display = 'block';
      }
    }
  }, [isFirstLogin]);
}
